import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { useAuth } from "../../contexts/AuthContext";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
};

export const Login: FC = () => {
    const navigate = useNavigate();
    const { login, loginWithCustomToken, loading } = useAuth();

    const [tabValue, setTabValue] = useState(0);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [customToken, setCustomToken] = useState('');
    const [error, setError] = useState('');

    const handleEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError('이메일과 비밀번호를 입력해주세요.');
            return;
        }

        try {
            await login(email, password);
            navigate('/library');
        } catch (err) {
            setError((err as Error).message || '로그인에 실패했습니다.');
        }
    };

    const handleCustomTokenLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!customToken) {
            setError('커스텀 토큰을 입력해주세요.');
            return;
        }

        try {
            await loginWithCustomToken(customToken);
            navigate('/library');
        } catch (err) {
            setError((err as Error).message || '커스텀 토큰 로그인에 실패했습니다.');
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'background.default',
                p: 2,
            }}
        >
            <Card
                sx={{
                    width: '100%',
                    maxWidth: 500,
                    boxShadow: 3,
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    <Typography
                        variant="h4"
                        align="center"
                        gutterBottom
                        fontWeight="bold"
                        color="primary"
                    >
                        단어장 관리자
                    </Typography>
                    <Typography
                        variant="body2"
                        align="center"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                    >
                        Firebase 인증으로 로그인하세요
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Tabs
                        value={tabValue}
                        onChange={(_, newValue) => setTabValue(newValue)}
                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                        centered
                        variant="fullWidth"
                    >
                        <Tab label="이메일 로그인" />
                        <Tab label="커스텀 토큰" />
                    </Tabs>

                    <TabPanel value={tabValue} index={0}>
                        <form onSubmit={handleEmailLogin}>
                            <TextField
                                fullWidth
                                label="이메일"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                margin="normal"
                                autoComplete="email"
                                autoFocus
                            />
                            <TextField
                                fullWidth
                                label="비밀번호"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                margin="normal"
                                autoComplete="current-password"
                            />
                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loading}
                                sx={{ mt: 3 }}
                            >
                                {loading ? <CircularProgress size={24} /> : '로그인'}
                            </Button>
                        </form>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <form onSubmit={handleCustomTokenLogin}>
                            <TextField
                                fullWidth
                                label="커스텀 토큰"
                                multiline
                                rows={4}
                                value={customToken}
                                onChange={(e) => setCustomToken(e.target.value)}
                                margin="normal"
                                placeholder="Firebase 커스텀 토큰을 입력하세요"
                            />
                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loading}
                                sx={{ mt: 3 }}
                            >
                                {loading ? <CircularProgress size={24} /> : '로그인'}
                            </Button>
                        </form>
                    </TabPanel>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            관리자 계정으로만 접근 가능합니다
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};
